import React, { useState } from 'react';
import {
  Box, Chip, FormControl, InputLabel, MenuItem, Paper, Select,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { megaphoneMumAPI } from '../../services/api';

/**
 * Live Trending — surfaces the MUM data at the top of the Episodes tab.
 * Two halves:
 *   - 30-day delivery sparkline per show
 *   - Top 5 episodes by today's downloads
 *
 * Refetches every 60s. MUM cron runs every 30 min, so this is a faster
 * cadence than the data actually changes — but the cost is one cached
 * Postgres aggregate per refresh and it keeps "feels live" UX.
 */
const fmtCount = (n: number | null | undefined): string => {
  if (n == null) return '—';
  if (n >= 10000) return (n / 1000).toFixed(0) + 'k';
  if (n >= 1000)  return (n / 1000).toFixed(1) + 'k';
  return n.toLocaleString();
};

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};


const TimelineChart: React.FC<{ site: string }> = ({ site }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['mum-live-timeline', site, 30],
    queryFn: () => megaphoneMumAPI.timeline(site, 30),
    refetchInterval: 60_000,
  });
  if (isLoading) return <Box sx={{ height: 140 }} />;
  const rows = (data?.timeline ?? []) as Array<{
    metric_date: string; downloads: number; streams: number; delivery: number;
  }>;
  if (rows.length === 0) return (
    <Typography variant="caption" sx={{ opacity: 0.5, display: 'block', p: 2 }}>
      No MUM data yet for {site}.
    </Typography>
  );
  const total30 = rows.reduce((sum, r) => sum + (r.delivery || 0), 0);
  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 1, px: 1 }}>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', opacity: 0.7 }}>
          {site.toUpperCase()}
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
          last 30d: <strong style={{ color: '#00ff41' }}>
            {total30.toLocaleString()}
          </strong> delivery
        </Typography>
      </Stack>
      <Box sx={{ width: '100%', height: 120 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={rows} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid stroke="#1e3a1e" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="metric_date" tickFormatter={fmtDate}
                   tick={{ fontSize: 10, fill: '#5aaa5a' }} />
            <YAxis tick={{ fontSize: 10, fill: '#5aaa5a' }}
                   tickFormatter={(v) => fmtCount(v)} />
            <Tooltip
              contentStyle={{
                background: '#0a0a0a', border: '1px solid #1e3a1e', fontSize: 12,
              }}
              labelStyle={{ color: '#00ff41' }}
            />
            <Area type="monotone" dataKey="downloads" stroke="#00ff41"
                  fill="#00ff41" fillOpacity={0.25} strokeWidth={1.5}
                  isAnimationActive={false} />
            <Area type="monotone" dataKey="streams" stroke="#1DB954"
                  fill="#1DB954" fillOpacity={0.2} strokeWidth={1.5}
                  isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};


const TopEpisodes: React.FC<{ site: string | undefined; days: number }> = ({
  site, days,
}) => {
  const { data, isLoading } = useQuery({
    queryKey: ['mum-top-episodes', site ?? 'all', days],
    queryFn: () => megaphoneMumAPI.topEpisodes({ site, days, limit: 10 }),
    refetchInterval: 60_000,
  });
  if (isLoading) return <Box sx={{ p: 2 }}>...</Box>;
  const eps = (data?.episodes ?? []) as Array<{
    site_slug: string;
    episode_id: string;
    title: string | null;
    publish_date: string | null;
    downloads: number;
    streams: number;
    delivery: number;
  }>;
  if (eps.length === 0) return (
    <Typography variant="caption" sx={{ opacity: 0.5, p: 2, display: 'block' }}>
      No episodes in window. MUM cron may not have run yet — try again in 30 min.
    </Typography>
  );
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 40 }}>#</TableCell>
          <TableCell>Episode</TableCell>
          <TableCell align="right" sx={{ width: 90 }}>Downloads</TableCell>
          <TableCell align="right" sx={{ width: 90 }}>Streams</TableCell>
          <TableCell align="right" sx={{ width: 90 }}>Delivery</TableCell>
          <TableCell sx={{ width: 90 }}>Released</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {eps.map((ep, i) => (
          <TableRow key={ep.episode_id} hover>
            <TableCell>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', opacity: 0.6 }}>
                {i + 1}
              </Typography>
            </TableCell>
            <TableCell>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={ep.site_slug === 'spoutlore' ? 'SL' : 'MB'}
                      size="small"
                      sx={{
                        bgcolor: 'transparent',
                        color: ep.site_slug === 'spoutlore' ? '#00ff41' : '#00ffff',
                        border: '1px solid',
                        borderColor: ep.site_slug === 'spoutlore' ? '#00ff41' : '#00ffff',
                        fontFamily: 'monospace', fontWeight: 700, fontSize: '0.7em',
                      }} />
                <Typography variant="body2" sx={{
                  overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 400,
                  whiteSpace: 'nowrap',
                }}>
                  {ep.title || ep.episode_id.slice(0, 12)}
                </Typography>
              </Stack>
            </TableCell>
            <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
              {fmtCount(ep.downloads)}
            </TableCell>
            <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
              {fmtCount(ep.streams)}
            </TableCell>
            <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
              {fmtCount(ep.delivery)}
            </TableCell>
            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8em', opacity: 0.7 }}>
              {fmtDate(ep.publish_date)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};


const LiveTrendingPanel: React.FC = () => {
  const [siteFilter, setSiteFilter] = useState<'all' | 'spoutlore' | 'mallbrats'>('all');
  const [days, setDays] = useState(30);
  const effectiveSite = siteFilter === 'all' ? undefined : siteFilter;

  return (
    <Paper sx={{
      p: 2, mb: 2, bgcolor: 'rgba(0,255,65,0.03)',
      border: '1px solid', borderColor: 'rgba(0,255,65,0.2)',
    }} variant="outlined">
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: 'monospace', color: '#00ff41' }}>
          // LIVE TRENDING
        </Typography>
        <Typography variant="caption" color="text.secondary">
          MUM private-API data, refreshed every 30 min (cron) + every 60s on this page
        </Typography>
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Show</InputLabel>
          <Select label="Show" value={siteFilter}
                  onChange={(e) => setSiteFilter(e.target.value as any)}>
            <MenuItem value="all">All shows</MenuItem>
            <MenuItem value="spoutlore">Spout Lore</MenuItem>
            <MenuItem value="mallbrats">Mall Brats</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Window</InputLabel>
          <Select label="Window" value={days}
                  onChange={(e) => setDays(Number(e.target.value))}>
            <MenuItem value={7}>7 days</MenuItem>
            <MenuItem value={30}>30 days</MenuItem>
            <MenuItem value={90}>90 days</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        {(siteFilter === 'all' || siteFilter === 'spoutlore') && (
          <Box sx={{ flex: 1 }}><TimelineChart site="spoutlore" /></Box>
        )}
        {(siteFilter === 'all' || siteFilter === 'mallbrats') && (
          <Box sx={{ flex: 1 }}><TimelineChart site="mallbrats" /></Box>
        )}
      </Stack>

      <Box sx={{ borderTop: '1px solid', borderColor: 'rgba(0,255,65,0.2)', pt: 1.5 }}>
        <Typography variant="caption" sx={{
          fontFamily: 'monospace', opacity: 0.7, mb: 1, display: 'block',
        }}>
          TOP EPISODES BY DELIVERY (last {days} days)
        </Typography>
        <TopEpisodes site={effectiveSite} days={days} />
      </Box>
    </Paper>
  );
};

export default LiveTrendingPanel;
